import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface EmbeddedChatContextValue {
  isSearchActive: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  openSearch: () => void;
  closeSearch: () => void;
}

const EmbeddedChatContext = createContext<EmbeddedChatContextValue | null>(null);

export const useEmbeddedChatContext = (): EmbeddedChatContextValue => {
  const context = useContext(EmbeddedChatContext);
  if (!context) {
    throw new Error('useEmbeddedChatContext must be used within EmbeddedChatProvider');
  }
  return context;
};

interface EmbeddedChatProviderProps {
  children: React.ReactNode;
}

export const EmbeddedChatProvider: React.FC<EmbeddedChatProviderProps> = ({ children }) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const openSearch = useCallback(() => {
    setIsSearchActive(true);
    setSearchQuery('');
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchActive(false);
    setSearchQuery('');
  }, []);

  const value = useMemo(
    () => ({
      isSearchActive,
      searchQuery,
      setSearchQuery,
      openSearch,
      closeSearch,
    }),
    [isSearchActive, searchQuery, openSearch, closeSearch]
  );

  return <EmbeddedChatContext.Provider value={value}>{children}</EmbeddedChatContext.Provider>;
};
